/**
 * Single source of truth for everything the session keeps in web storage.
 *
 * Before this module the key list was duplicated in three places with different
 * contents, so a logout could leave stale keys behind and a login could wipe
 * keys another tab still needed. Every write goes through here and bumps a
 * monotonic token version, which lets callers tell "my token is one generation
 * behind" apart from "this session is dead".
 */
import generateUUID from './generateUUID';
import { publishSessionEvent, SESSION_EVENTS } from './sessionChannel';

export const STORAGE_KEYS = {
  accessToken: 'access_token',
  refreshToken: 'refresh_token',
  tokenVersion: 'token_version',
  tokenIssuedAt: 'token_issued_at',
  login: 'login',
  loginUsername: 'login_username',
  agency: 'agency',
  agencyValidated: 'agency_validated',
  leftNavMenu: 'leftNavMenu',
  lastActiveTime: 'lastActiveTime',
  loginResponseCode: 'login_response_code',
  sessionActive: 'session_active',
  tempbotSessionId: 'tempbot_session_id'
};

const SESSION_KEYS = Object.values(STORAGE_KEYS);

/**
 * Lives outside STORAGE_KEYS on purpose: it identifies the browser, not the
 * session, so a logout must not rotate it. A fresh id after every logout would
 * make the server see a new device each time the same person signs in again.
 */
const DEVICE_ID_KEY = 'device_id';

export const DEVICE_ID_HEADER = 'X-Device-Id';

const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const write = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    /* storage unavailable (private mode, quota): callers degrade gracefully */
  }
};

/**
 * Identifier of this browser, shared by every tab and kept across sessions.
 * Created on first use. When storage is unavailable the id is regenerated on each
 * call, which the server sees as a new device: degraded, never broken.
 */
export const getDeviceId = () => {
  const stored = read(DEVICE_ID_KEY);
  if (stored) return stored;

  const created = generateUUID();
  write(DEVICE_ID_KEY, created);
  return created;
};

/** Header every request to the API carries so the server can tell devices apart. */
export const getDeviceHeaders = () => ({ [DEVICE_ID_HEADER]: getDeviceId() });

export const getAccessToken = () => read(STORAGE_KEYS.accessToken);

export const getRefreshToken = () => read(STORAGE_KEYS.refreshToken);

/** Monotonic counter identifying the current token generation across all tabs. */
export const getTokenVersion = () => {
  const version = parseInt(read(STORAGE_KEYS.tokenVersion), 10);
  return Number.isFinite(version) ? version : 0;
};

export const hasTokens = () => !!getAccessToken() && !!getRefreshToken();

/** Epoch ms when this tab last stored a token pair. Fallback for opaque tokens. */
export const getTokenIssuedAt = () => {
  const issuedAt = parseInt(read(STORAGE_KEYS.tokenIssuedAt), 10);
  return Number.isFinite(issuedAt) ? issuedAt : null;
};

/**
 * Stores a freshly issued token pair and announces the new generation.
 *
 * An omitted `refreshToken` keeps the current one, as OAuth2 prescribes: this
 * backend's `/oauth/validate` answers with an access token only, and the refresh
 * token minted at login stays valid for the session it opens.
 */
export const commitTokens = ({ accessToken, refreshToken }) => {
  if (!accessToken) return null;

  const version = getTokenVersion() + 1;

  write(STORAGE_KEYS.accessToken, accessToken);
  write(STORAGE_KEYS.tokenVersion, String(version));
  write(STORAGE_KEYS.tokenIssuedAt, String(Date.now()));

  if (refreshToken) write(STORAGE_KEYS.refreshToken, refreshToken);

  publishSessionEvent(SESSION_EVENTS.TOKENS_UPDATED, { version });
  return version;
};

/** Username typed at login, so a new login from the same screen does not ask for it again. */
export const getLoginUsername = () => read(STORAGE_KEYS.loginUsername) || '';

export const storeLoginUsername = (username) => {
  if (username) write(STORAGE_KEYS.loginUsername, username);
};

/** Restarts the idle clock shared by every tab (read by TimeOutModal). */
export const markActivity = () => write(STORAGE_KEYS.lastActiveTime, String(Math.floor(Date.now() / 1000)));

/** Removes every session key. Keys outside the list are left untouched on purpose. */
export const clearSession = () => {
  SESSION_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      /* nothing else to do; the redirect that follows drops the tab anyway */
    }
  });

  try {
    sessionStorage.clear();
  } catch (error) {
    /* same as above */
  }
};
